'use client';

import { useTranslation } from 'react-i18next';

/**
 * Delta (Nadena) bespoke Terms & Conditions.
 *
 * Content is Nadena's real membership terms, transcribed verbatim from
 * nadena-nc.com/terms-and-conditions + /disclaimer (14 clauses + member and
 * guardian undertakings, including the 3-day refund clause #14). The operating
 * entity is شركة تطوير للخدمات التعليمية (Tatweer Educational Services).
 *
 * Arabic is authoritative (source language); an English rendering is provided
 * for the EN locale. This replaces the generic platform template for the
 * `delta` subdomain only.
 */

const MEMBERSHIP_CLAUSES_AR = [
  'يجب حفظ بطاقة العضوية في مكان آمن، ولا يجوز استخدامها إلا من قِبل حامل البطاقة.',
  'يجب على الأعضاء الالتزام بقواعد النادي ولوائحه العامة، بما في ذلك الالتزام بأوقات الصالة، والمحافظة على الآداب العامة، واتباع احتياطات الصحة والسلامة، والحفاظ على ممتلكات النادي.',
  'لا يُسمح لغير الأعضاء باستخدام مرافق النادي.',
  'يُسمح فقط للوالدين باستخدام المناطق المخصصة لهم من قبل إدارة النادي، مع الالتزام بجميع التعليمات الصادرة.',
  'يلتزم الأعضاء بارتداء ملابس مناسبة للمشاركة في الأنشطة، على أن تكون متوافقة مع لائحة الذوق العام، وبما يتلاءم مع طبيعة النشاط الممارس.',
  'يجب تقديم بطاقة عضوية سارية المفعول عند الطلب.',
  'العضوية غير قابلة للتحويل.',
  'النادي غير مسؤول عن فقدان المتعلقات الشخصية للأعضاء.',
  'النادي غير مسؤول عن أي إصابة أو ضرر قد يحدث للمشترك نتيجة عدم الالتزام بالتعليمات، أو بسبب خطأ شخصي من المشارك نفسه، أو نتيجة ممارسة نشاط غير مناسب لحالته الصحية في حال عدم الإفصاح عنها في استمارة التسجيل.',
  'يحق لأندية الحي توثيق أنشطتها ونشرها عبر مختلف الوسائل الإعلامية ومنصات التواصل الاجتماعي، ويُعد حضور أو مشاركة الأفراد موافقة صريحة على التصوير والنشر دون أي مطالبات بحقوق أو تعويضات.',
  'لإدارة النادي الحق في تعليق أو إلغاء العضوية لأي سلوك أو تصرف يُعد مخالفًا للوائح النادي أو الأنشطة المقدمة أو الآداب العامة، دون تعويض.',
  'يتم تسجيل المشترك في نظام الاشتراك اليومي وفق الرسوم المعتمدة والمعلنة عبر البوابة، ويُفعل الاشتراك لمدة (٢٤) ساعة فقط من وقت التسجيل، وتنتهي صلاحية الدخول بانتهاء المدة.',
  'يتم إصدار بطاقة بدل فاقد برسوم إضافية.',
  'يحق للعضو طلب استرجاع مبلغ الاشتراك خلال ٣ أيام من تاريخ السداد كحد أقصى، بشرط عدم تفعيل العضوية أو استخدام أي من مرافق النادي.',
];

const MEMBERSHIP_CLAUSES_EN = [
  'The membership card must be kept in a safe place and may only be used by the cardholder.',
  'Members must comply with the club’s rules and general regulations, including adhering to hall timings, observing public etiquette, following health and safety precautions, and preserving the club’s property.',
  'Non-members are not permitted to use the club’s facilities.',
  'Only parents may use the areas designated for them by the club’s management, while adhering to all issued instructions.',
  'Members must wear attire appropriate for participation in activities, in line with the public decency regulations and suitable for the nature of the activity practised.',
  'A valid membership card must be presented upon request.',
  'Membership is non-transferable.',
  'The club is not responsible for the loss of members’ personal belongings.',
  'The club is not responsible for any injury or harm that may occur to the participant as a result of failing to follow instructions, personal error by the participant, or practising an activity unsuitable for their health condition where it was not disclosed in the registration form.',
  'The Neighborhood Clubs reserve the right to document and publish their activities across various media outlets and social platforms; attendance or participation is deemed explicit consent to photography and publication, with no claims to rights or compensation.',
  'The club’s management reserves the right to suspend or cancel membership for any conduct deemed in violation of the club’s regulations, the activities offered, or public etiquette, without compensation.',
  'The subscriber is registered under the daily subscription system per the approved fees announced via the portal; the subscription is activated for (24) hours only from the time of registration, and access expires at the end of that period.',
  'A replacement card is issued for an additional fee.',
  'A member may request a refund of the subscription amount within a maximum of 3 days from the date of payment, provided the membership has not been activated and none of the club’s facilities have been used.',
];

export function DeltaTermsOfService() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  return isArabic ? <ArabicTerms /> : <EnglishTerms />;
}

function ArabicTerms() {
  return (
    <div dir="rtl" className="prose prose-lg dark:prose-invert max-w-none text-right">
      <h2 className="mt-8 mb-4 text-2xl font-bold">شروط وأحكام العضوية</h2>
      <ol className="list-decimal space-y-3 pr-6">
        {MEMBERSHIP_CLAUSES_AR.map((clause, i) => (
          <li key={i}>{clause}</li>
        ))}
      </ol>

      <h2 className="mt-8 mb-4 text-2xl font-bold">إقرار وتعهد العضو</h2>
      <p>
        أُقر بمراجعتي لجميع التعليمات والإجراءات الواردة في هذا النموذج وما يترتب
        عليها، وأوافق على الانضمام إلى النادي.
      </p>
      <p>
        وبموجب هذا، أُخلي مسؤولية شركة تطوير للخدمات التعليمية وممثليها، وإدارة
        النادي وموظفيه، من أي مسؤولية مادية أو معنوية ناتجة عن أي ضرر قد يلحق بي
        نتيجة المشاركة في أي نشاط داخل النادي، كما أُقر بالإفصاح عن أي أمراض أو
        أعراض أو سلوكيات شخصية.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">
        إقرار ولي الأمر (ينطبق على من هم دون سن السادسة عشرة)
      </h2>
      <p>
        اطلعتُ على التعليمات والقواعد المذكورة أعلاه، وبصفتي ولي أمر الشخص المذكور
        اسمه في هذا النموذج، أوافق على قبوله وانضمامه إلى النادي.
      </p>
      <p>
        وبموجب هذا، أُخلي مسؤولية شركة تطوير للخدمات التعليمية وممثليها، وإدارة
        النادي وموظفيه، من أي مسؤولية مادية أو أدبية قد تترتب على أي ضرر يحدث في
        الحالات الناتجة عن خطأ من المشارك نفسه، أو عدم التزامه بالتعليمات، أو
        ممارسة نشاط غير مناسب لحالته الصحية.
      </p>
      <p>
        كما أُقر بأنه تم الإفصاح عن أي أمراض أو أعراض أو سلوكيات خاصة بالعضو لإدارة
        النادي.
      </p>
    </div>
  );
}

function EnglishTerms() {
  return (
    <div className="prose prose-lg dark:prose-invert max-w-none">
      <h2 className="mt-8 mb-4 text-2xl font-bold">Membership Terms &amp; Conditions</h2>
      <ol className="list-decimal space-y-3 pl-6">
        {MEMBERSHIP_CLAUSES_EN.map((clause, i) => (
          <li key={i}>{clause}</li>
        ))}
      </ol>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Member Acknowledgement &amp; Undertaking</h2>
      <p>
        I acknowledge that I have reviewed all the instructions and procedures set
        out in this form and their implications, and I agree to join the club.
      </p>
      <p>
        I hereby release Tatweer Educational Services Company and its
        representatives, and the club’s management and staff, from any material or
        moral liability arising from any harm that may befall me as a result of
        participating in any activity within the club. I also acknowledge my
        obligation to disclose any illnesses, symptoms, or personal behaviours.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">
        Guardian Acknowledgement (applies to those under 16 years of age)
      </h2>
      <p>
        I have reviewed the instructions and rules stated above, and as the
        guardian of the person named in this form, I agree to their acceptance and
        enrolment in the club.
      </p>
      <p>
        I hereby release Tatweer Educational Services Company and its
        representatives, and the club’s management and staff, from any material or
        moral liability that may arise from any harm occurring in cases resulting
        from the participant’s own error, their failure to follow instructions, or
        their practice of an activity unsuitable for their health condition.
      </p>
      <p>
        I also acknowledge that any illnesses, symptoms, or particular behaviours of
        the member have been disclosed to the club’s management.
      </p>
    </div>
  );
}
